import { flatten, orderBy, range, uniq } from "lodash";
import { getEndOfPlanYear, getRetirementYear } from "./utils";
import {
  useFinancialPlanCombinedDetail,
  useFinancialPlanExpenseTimeline,
  useFinancialPlanGoals,
} from "@modules/financialplan/queries";

import { IMPORTANCE_GROUP, IMPORTANCE_GROUP_BY_IMPORTANCE } from "./constants";
import { ScenarioID } from "@modules/mgp-sso/types";
import { currencyFormatter } from "@common/utils";
import { keyBy, isNumber } from "lodash";
import { useMemo } from "react";
import { EnrichedFinancialPlanYear } from "./types";
import moment from "moment";

const useFinancialPlanDates = () => {
  const { data: combinedDetail, isLoading: isLoadingCombinedDetail } =
    useFinancialPlanCombinedDetail({
      scenario: "current",
      quickDate: "inception",
    });
  const planInceptionDate = combinedDetail?.timestamp;
  const { data: combinedDetailRecc, isLoading: isLoadingCombinedDetailRecc } =
    useFinancialPlanCombinedDetail({
      scenario: "recommended",
      quickDate: "most-recent",
    });
  const planMostRecentDate = combinedDetailRecc?.timestamp;
  const {
    data: expenseTimeline,
    refetch,
    isLoading: isLoadingExpenseTimeline,
  } = useFinancialPlanExpenseTimeline({ scenario: ScenarioID.Current });
  // we use the "first" retirement year in the series, between client/co-client.
  const retirementYear = useMemo(
    () => getRetirementYear(expenseTimeline),
    [expenseTimeline]
  );
  const planningYear = useMemo(
    () => getEndOfPlanYear(expenseTimeline),
    [expenseTimeline]
  );
  return {
    planMostRecentDate,
    planInceptionDate,
    retirementYear,
    planningYear,
    refetch,
    isLoading:
      isLoadingExpenseTimeline ||
      isLoadingCombinedDetail ||
      isLoadingCombinedDetailRecc,
  };
};

const applyInflationDiscount = (
  planYearPortfolio: Forme.BeginningPortfolio,
  yearCount: number
) => {
  const inflationRate = 0.0253; // asof Aug 22, 2022
  return {
    ...planYearPortfolio,
    total: {
      ...planYearPortfolio.total,
      total:
        (planYearPortfolio.total?.total ?? 0) /
        Math.pow(1 + inflationRate, yearCount),
    },
  };
};

const useCombinedScenarios = (options = {}) => {
  const { isLoading: isLoadingDates } = useFinancialPlanDates();
  const {
    data: recommended,
    isLoading: isLoadingRecommended,
    refetch: refetchFinancialPlanRecommended,
  } = useFinancialPlanCombinedDetail(
    { scenario: ScenarioID.Recommended, quickDate: "most-recent" },
    options
  );
  const {
    data: current,
    isLoading: isLoadingCurrent,
    refetch: refetchFinancialPlanCurrent,
  } = useFinancialPlanCombinedDetail(
    { scenario: ScenarioID.Current, quickDate: "most-recent" },
    options
  );

  const currentYears = current?.data?.years;
  const recommendedYears = recommended?.data?.years;

  const refetchCombinedScenarios = (() => {
    refetchFinancialPlanRecommended();
    refetchFinancialPlanCurrent();
  }) as () => Promise<unknown>;

  const combinedScenarios = useMemo(() => {
    if (!recommendedYears || !currentYears) return [];

    const years = uniq(
      flatten([
        recommendedYears.map((plan) => plan.year),
        currentYears.map((plan) => plan.year),
      ])
    );

    const reccPlanYearByYear = keyBy(recommendedYears, "year");
    const currPlanYearByYear = keyBy(currentYears, "year");

    const data: EnrichedFinancialPlanYear[] = [];

    const todaysMonthAndDate = moment().format("MM-DD");
    const todaysYear = moment().format("YYYY");
    // iterate in reverse to apply deflationary effect to convert future dollars to "current".
    years.forEach((year, index) => {
      const reccPlanYear = reccPlanYearByYear[year];
      const currPlanYear = currPlanYearByYear[year];
      if (!reccPlanYear || !currPlanYear) return;

      const isThisYear = String(year) === todaysYear;
      const planYear: EnrichedFinancialPlanYear = {
        ...currPlanYear,
        date: isThisYear
          ? `${todaysYear}-${todaysMonthAndDate}`
          : `${year}-01-01`,
        beginningPortfolio: applyInflationDiscount(
          currPlanYear.beginningPortfolio,
          isThisYear ? 0 : index
        ),
        endingPortfolio: applyInflationDiscount(
          currPlanYear.endingPortfolio,
          isThisYear ? 1 : index + 1
        ),
        recommendedBeginningPortfolio: applyInflationDiscount(
          reccPlanYear.beginningPortfolio,
          isThisYear ? 0 : index
        ),
        recommendedEndingPortfolio: applyInflationDiscount(
          reccPlanYear.endingPortfolio,
          isThisYear ? 1 : index + 1
        ),
      };

      data.push(planYear);
    });

    // add extra data point for ending portfolio
    const lastRecommendedYear = recommendedYears.at(-1);
    const lastCurrentYear = currentYears.at(-1);
    if (lastRecommendedYear && lastCurrentYear) {
      const lastYear: EnrichedFinancialPlanYear = {
        ...lastCurrentYear,
        // Beginning of next year is same as 12-31 of this year.
        year: lastCurrentYear.year + 1,
        date: `${lastCurrentYear.year + 1}-01-01`,
        agesOrEvents: "Plan Ends",
        beginningPortfolio: applyInflationDiscount(
          lastCurrentYear.beginningPortfolio,
          years.length - 1
        ),
        endingPortfolio: applyInflationDiscount(
          lastCurrentYear.endingPortfolio,
          years.length
        ),
        recommendedBeginningPortfolio: applyInflationDiscount(
          lastRecommendedYear.beginningPortfolio,
          years.length - 1
        ),
        recommendedEndingPortfolio: applyInflationDiscount(
          lastRecommendedYear.endingPortfolio,
          years.length
        ),
      };
      data.push(lastYear);
    }

    return data;
  }, [recommendedYears, currentYears]);
  return {
    combinedScenarios,
    isLoading: isLoadingCurrent || isLoadingRecommended || isLoadingDates,
    refetchCombinedScenarios,
  };
};

const useFinancialPlanSummaryValues = () => {
  const currentYear = moment().format("YYYY");

  const { combinedScenarios, isLoading: isLoadingCombinedScenarios } =
    useCombinedScenarios();
  const { retirementYear, isLoading: isLoadingRetirementYear } =
    useFinancialPlanDates();

  const currentMarketValueUnformatted = useMemo(() => {
    // Most Recent Recommended Beginning Portfolio
    const currentYearScenarios = combinedScenarios.filter(
      (scenario) => scenario.year.toString() === currentYear
    );
    const mostRecentScenario = orderBy(
      currentYearScenarios,
      (scenario) => scenario.date,
      ["asc"]
    ).at(-1);
    return mostRecentScenario?.recommendedBeginningPortfolio?.total?.total;
  }, [combinedScenarios, currentYear]);

  const currentMarketValue = useMemo(() => {
    return currencyFormatter(currentMarketValueUnformatted);
  }, [currentMarketValueUnformatted]);

  const excessAtPlansEndUnformatted = useMemo(() => {
    return (
      combinedScenarios?.at(-1)?.recommendedEndingPortfolio?.total?.total ?? 0
    );
  }, [combinedScenarios]);

  const excessAtPlansEnd = useMemo(() => {
    return currencyFormatter(excessAtPlansEndUnformatted);
  }, [excessAtPlansEndUnformatted]);

  const isLoading = useMemo(
    () =>
      isLoadingCombinedScenarios ||
      isLoadingRetirementYear ||
      !isNumber(currentMarketValueUnformatted),
    [
      isLoadingCombinedScenarios,
      isLoadingRetirementYear,
      currentMarketValueUnformatted,
    ]
  );

  return {
    currentMarketValue,
    excessAtPlansEnd,
    isLoading,
  };
};

const useFinancialGoalsValues = () => {
  const { data: financialGoals, isLoading: isLoadingFinancialGoals } =
    useFinancialPlanGoals({ scenario: ScenarioID.Current });

  const goals = useMemo(() => {
    return financialGoals ?? [];
  }, [financialGoals]);

  // TODO duped between Charts and Goals
  const goalsById: Record<string, Forme.FinancialGoal> = useMemo(() => {
    return keyBy(financialGoals, "id");
  }, [financialGoals]);

  const goalsByImportanceGroup = orderBy(goals, "totalExpenseAmount", [
    "desc",
  ]).reduce((memo, goal) => {
    const group =
      IMPORTANCE_GROUP_BY_IMPORTANCE[goal.importance] ?? IMPORTANCE_GROUP.Wish;
    if (!memo[group]) memo[group] = [];
    memo[group].push(goal);
    return memo;
  }, {} as Record<Forme.ImportanceGroup, Forme.FinancialGoal[]>);

  return { goals, goalsById, goalsByImportanceGroup, isLoadingFinancialGoals };
};

const useExpenseTimelineValues = (selectedScenario: ScenarioID) => {
  const { data: expenseTimelineData, isLoading } =
    useFinancialPlanExpenseTimeline({ scenario: selectedScenario });

  const { minYear, maxYear } = useMemo(() => {
    return (expenseTimelineData?.expenses ?? []).reduce(
      (memo, expense) => {
        const minYear = Math.min(
          ...(Object.keys(expense?.expensesByYear) ?? []).map((year) =>
            parseInt(year)
          )
        );
        if (minYear && (minYear < memo.minYear || memo.minYear === 0))
          memo.minYear = minYear;
        const maxYear = Math.max(
          ...(Object.keys(expense?.expensesByYear) ?? []).map((year) =>
            parseInt(year)
          )
        );
        if (maxYear && (maxYear > memo.maxYear || memo.maxYear === 0))
          memo.maxYear = maxYear;
        return memo;
      },
      { minYear: 0, maxYear: 0 }
    );
  }, [expenseTimelineData]);

  const expenseTimeline = useMemo(() => {
    if (minYear === 0 || maxYear === 0) {
      return [];
    }
    return range(minYear, maxYear).map((year) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const expenseYear: any = { year };
      expenseTimelineData?.expenses.forEach((expense) => {
        expenseYear[expense.name] = expense.expensesByYear[year]?.amount ?? 0;
      });
      return expenseYear;
    });
  }, [expenseTimelineData, minYear, maxYear]);

  return {
    expenseTimeline,
    isLoading,
  };
};

const useProjectedFinancialPlanChartData = () => {
  const { combinedScenarios } = useCombinedScenarios();
  return useMemo(() => {
    return (
      combinedScenarios?.map?.((year, index) => {
        const isLastYear = index === combinedScenarios.length - 1;
        return {
          name: year.date,
          date: moment(year.date).toDate().getTime(),
          current: isLastYear
            ? year.endingPortfolio?.total?.total || 0
            : year.beginningPortfolio?.total?.total || 0,
          recommended: isLastYear
            ? year.recommendedEndingPortfolio?.total?.total || 0
            : year.recommendedBeginningPortfolio?.total?.total || 0,
          agesOrEvents: year.agesOrEvents.match(/\d+/)
            ? null
            : year.agesOrEvents,
        };
      }) || []
    );
  }, [combinedScenarios]);
};

export {
  useFinancialPlanDates,
  useCombinedScenarios,
  useFinancialPlanSummaryValues,
  useFinancialGoalsValues,
  useExpenseTimelineValues,
  useProjectedFinancialPlanChartData,
};
